import type { CompanyType } from './company.model';
import type { LicenseBatchDto } from './license.model';

export interface CompanyRoleType {
  role?: CompanyRoleValue;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  companyRole?: CompanyRoleValue;
  licenseBatches?: LicenseBatchDto[];
}

export type CompanyRoleValue = 0 | 1 | 2 | '0' | '1' | '2';

/** A cross-company role, as returned by the backend. Absent for a regular user. */
export enum PrivilegedRole {
  MASTERADMIN = 'MASTERADMIN',
  SUPPORT = 'SUPPORT',
  PRODUCTION = 'PRODUCTION',
}

/** Just enough of a profile to decide what a cross-company role may do. */
export interface PrivilegedProfile {
  isMasteradmin?: boolean;
  privilegedRole?: PrivilegedRole | null;
}

/**
 * Whether this user manages devices beyond a single company: a masteradmin over all of them, a
 * production engineer over the scope configured for them. The backend decides what that scope
 * actually covers - this only decides whether the controls are offered at all.
 */
export function managesDevicesAcrossCompanies(
  profile?: PrivilegedProfile | null,
): boolean {
  return (
    !!profile &&
    (profile.isMasteradmin === true ||
      profile.privilegedRole === PrivilegedRole.PRODUCTION)
  );
}

/**
 * Whether this user reads every company, device, license, batch and credential rather than only
 * their own company's: a masteradmin or a support user. It opens the administrative sections and
 * the routes below them; the edit controls inside them stay gated on {@code isMasteradmin}, since
 * a support user may only look.
 */
export function readsDataAcrossCompanies(
  profile?: PrivilegedProfile | null,
): boolean {
  return (
    !!profile &&
    (profile.isMasteradmin === true ||
      profile.privilegedRole === PrivilegedRole.SUPPORT)
  );
}

/**
 * Whether this user is allowed to open links in the Log UI.
 */
export function allowOpenLogUILinks(
  profile?: PrivilegedProfile | null,
): boolean {
  return (
    !!profile &&
    (profile.isMasteradmin === true ||
      profile.privilegedRole === PrivilegedRole.SUPPORT ||
      profile.privilegedRole === PrivilegedRole.PRODUCTION)
  );
}

/**
 * Whether this user may start an impersonation. A masteradmin may act as anyone, a support user
 * only as a regular user - the backend rejects a privileged target either way.
 */
export function mayStartImpersonation(
  profile?: PrivilegedProfile | null,
): boolean {
  return (
    !!profile &&
    (profile.isMasteradmin === true ||
      profile.privilegedRole === PrivilegedRole.SUPPORT)
  );
}

export interface ProfileType {
  id: string;
  companyId?: string;
  company?: CompanyType;
  companyRole?: CompanyRoleType | CompanyRoleValue;
  email?: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  companyName: string;
  country: string;
  postalCode?: string;
  isMasteradmin: boolean;
  privilegedRole?: PrivilegedRole | null;
  industry: string;
  fieldOfApplication?: string;
  fieldsOfApplication?: string[];
  stateOrProvince: string;
  avatar: string; //TODO: Delete
  licenseBatches?: LicenseBatchDto[];
  /** The masteradmin acting as this user, on an impersonated request or on an attribution. */
  impersonatedBy?: Partial<ProfileType>;
  /** Only on the own profile of an impersonated request. */
  impersonationExpiresAt?: string;
}
export enum UserRole {
  ADMIN = 'ADMIN',
  PILOT = 'PILOT',
  MAINTAINER = 'MAINTAINER',
}
